import { useMemo } from "react";
import { usePermissions } from "./usePermissions";
import { firebaseHelper } from "./firebase";
import { Permissions } from "ts-interfaces";

interface TeamMembership {
  teamId: string | null;
  isTeamMember: boolean;
  isTeamAdmin: boolean;
  permissions: Permissions | null;
  loading: boolean;
}

const getPrimaryTeamId = (permissions: Permissions | null) =>
  permissions?.teamsAdmin?.[0] ?? permissions?.teamsDesigner?.[0] ?? null;

const isAdminForTeam = (
  permissions: Permissions | null,
  teamId: string | null,
) => {
  if (!teamId || !permissions) return false;
  return permissions.teamsAdmin?.includes(teamId) ?? false;
};

/**
 * Hook to get team membership information for a user
 */
export const useTeamMembership = (uid?: string | null): TeamMembership => {
  const { permissions, loading } = usePermissions(uid, firebaseHelper.db);

  const teamId = useMemo(() => getPrimaryTeamId(permissions), [permissions]);
  const isTeamMember = Boolean(teamId);
  const isTeamAdmin = useMemo(
    () => isAdminForTeam(permissions, teamId),
    [permissions, teamId],
  );

  return {
    teamId,
    isTeamMember,
    isTeamAdmin,
    permissions,
    loading,
  };
};
